import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Stack, Typography, IconButton } from '@mui/material';
import { useNavigationStore } from '../../../../../store';
import {
  Background,
  Wrapper,
  Button,
} from '@/components/navigation/NavigationIntroScreen/components/Popup/styles';
import DeviceManager from '@/components/util/deviceManager';
import {
  ChevronLeftIcon,
  FinishIcon,
  PhoneIcon,
} from '@/components/navigation/NavigationIntroScreen/components/Popup/Icons';
import { ShareContainer } from '@/components/navigation/NavigationIntroScreen/components/Popup/components/ShareContainer';
import { DetailedRoutes } from './DetailedRoutes';
import { RatingTooltip } from '@/components/navigation/NavigationIntroScreen/components/Popup/RatingTooltip';
import { navigationTranslationState } from '../../../../../store';
import { TourSharePopupProps } from './types';
import { trimDescription as trimText } from '@/components/navigation/search-bar/util';

const IMAGE_HEIGHT = 192;

export const NavigationTourSharePopup: React.FC<TourSharePopupProps> = (props) => {
  const {
    open,
    setOpen,
    setPopupHeight,
    popupTitle,
    qrEnabled,
    qrTitle,
    smsEnabled,
    smsTitle,
    smsText,
    startingPoint,
    setBackgroundVisible,
    setChoicePopupOpen,
    setNavigationDestination,
    item,
    detailedRouteEnabled,
    target,
    detailedRoutes,
    showDetailedRoutesOnly,
    closeDetailedRoutesList,
    setActionPathNavbarPlayButtonType,
    navigationManager,
    navigationType,
    actionPathFinished,
    handleBackToSearchBar,
    handleReplay,
    tour,
    ratingEnabled,
    startingPointTitle,
    finishPointTitle,
    rtl,
    country,
    showCaseTemp,
    policy,
    setMobilePosition,
  } = props;
  const [isVisible, setIsVisible] = useState(open);
  const [showShareButton, setShowShareButton] = useState(false);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const titleContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = DeviceManager.isMobile();
  const tooltipContainerRef = useRef(null);

  const translation = useNavigationStore((state) => state.navigationTranslation);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (open) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useLayoutEffect(() => {
    const textHeight = textContainerRef.current?.offsetHeight;
    const titleHeight = titleContainerRef.current?.offsetHeight;
    const height = textHeight + titleHeight + IMAGE_HEIGHT;
    if (height) setPopupHeight(height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  useEffect(() => {
    if (actionPathFinished) {
      setShowShareButton(true);
    } else {
      setShowShareButton(false);
    }
  }, [actionPathFinished]);

  return (
    <>
      <Background
        id="tour-share-popup"
        open={open}
        isVisible={isVisible}
        style={{
          background: 'none',
          backdropFilter: 'blur(0px)',
          position: isMobile ? 'relative' : 'fixed',
          marginTop: isMobile ? 'unset' : '20px',
          height: isMobile ? '100%' : 'auto',
          display: open ? '' : 'none',
          width: isMobile ? '100%' : 'fit-content',
        }}
      >
        <Wrapper
          open={open}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: isMobile ? 'relative' : 'static',
            padding: '0px',
            width: isMobile ? 'auto' : '340px',
            height: isMobile ? '100%' : 'auto',
            border: isMobile ? 'none' : '1px solid #F7F7F7',
            transform: 'none',
            animation: 'none',
            marginLeft: isMobile ? '0' : '20px',
            background: isMobile ? 'none' : '#F7F7F7',
            borderRadius: '14px',
          }}
        >
          <Stack
            id="titles"
            ref={titleContainerRef}
            sx={{
              display: { xs: actionPathFinished ? 'none' : 'flex', sm: 'flex' },
              p: '14px',
              background: '#F7F7F7',
              alignItems: 'center',
              justifyContent: {
                xs: showDetailedRoutesOnly ? 'space-between' : 'center',
                sm: 'space-between',
              },
              borderBottom: '1px solid var(--Gray-300, #D0D5DD)',
              borderTopLeftRadius: '14px',
              borderTopRightRadius: '14px',
              fontWeight: rtl ? 500 : 600,
            }}
            direction="row"
          >
            <IconButton
              sx={{
                borderRadius: '1rem',
                padding: '4px',
                background: '#EAECF0',
                width: '32px',
                height: '32px',
                display: { xs: showDetailedRoutesOnly ? 'inline-flex' : 'none', sm: 'inline-flex' },
              }}
              onClick={() => {
                if (showDetailedRoutesOnly) {
                  closeDetailedRoutesList();
                } else if (showShareButton && !actionPathFinished) {
                  setShowShareButton(false);
                } else {
                  setOpen(false);
                  setBackgroundVisible(true);
                  setChoicePopupOpen(true);
                }
              }}
            >
              <ChevronLeftIcon style={{}} />
            </IconButton>
            {showShareButton && !actionPathFinished && !DeviceManager.isMobile() ? (
              <Typography
                fontSize="16px"
                fontWeight={rtl ? 500 : 600}
                lineHeight="24px"
                sx={{ direction: rtl ? 'rtl' : 'ltr', color: '#000000' }}
              >
                {translation.navShareToPhone || 'Share to your phone'}
              </Typography>
            ) : (
              <Stack textAlign="center">
                <Stack
                  direction="row"
                  sx={{ direction: rtl ? 'rtl' : 'ltr' }}
                  justifyContent="center"
                >
                  <Typography color="#667085" fontWeight={rtl ? 400 : 500} fontSize="16px">
                    {translation?.navFrom || 'From'}
                  </Typography>
                  <Typography color="#667085" fontWeight={rtl ? 400 : 500} fontSize="16px">
                    : {trimText(startingPointTitle, 20)}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  sx={{ direction: rtl ? 'rtl' : 'ltr' }}
                  justifyContent="center"
                >
                  <Typography
                    fontSize="16px"
                    fontWeight={rtl ? 400 : 500}
                    lineHeight="24px"
                    color="var(--Black, #000)"
                  >
                    {translation?.navTo || 'To'}
                  </Typography>
                  <Typography
                    fontSize="16px"
                    fontWeight={rtl ? 400 : 500}
                    lineHeight="24px"
                    color="var(--Black, #000)"
                  >
                    : {trimText(finishPointTitle, 20)}
                  </Typography>
                </Stack>
              </Stack>
            )}
            <IconButton
              sx={{
                width: '32px',
                height: '32px',
                color: 'black',
                fontSize: '16px',
                borderRadius: '1rem',
                marginRight: '5px',
                backgroundColor: '#eaecf0',
                visibility: 'hidden',
              }}
            >
              <i className="fa-solid fa-chevron-right" />
            </IconButton>
          </Stack>
          <Stack
            ref={tooltipContainerRef}
            sx={{
              padding: '14px',
              background: '#F2F4F7',
              borderTop: '1px solid var(--Gray-200, #EAECF0)',
              borderBottomLeftRadius: '14px',
              borderBottomRightRadius: '14px',
            }}
          >
            {actionPathFinished && (
              <RatingTooltip
                tooltipContainerRef={tooltipContainerRef}
                tour={tour}
                target={target}
                actionPathFinished={actionPathFinished}
                ratingEnabled={ratingEnabled}
                rtl={rtl}
                setMobilePosition={setMobilePosition}
              >
                <Stack
                  sx={{
                    background: '#FFF',
                    alignItems: 'center',
                    padding: '15px 10px',
                    gap: '10px',
                    textAlign: 'center',
                    borderRadius: '10px',
                    marginBottom: '12px',
                    maxHeight: '104px',
                    overflow: 'auto',
                  }}
                >
                  <Stack>
                    <FinishIcon style={{}} />
                  </Stack>
                  <Typography
                    sx={{
                      textAlign: 'center',
                      textOverflow: 'ellipsis',
                      fontSize: '16px',
                      fontWeight: 600,
                      lineHeight: '24px',
                      direction: rtl ? 'rtl' : 'ltr',
                    }}
                  >
                    {translation.navYouArrived || 'You have arrived to destination!'}
                  </Typography>
                </Stack>
              </RatingTooltip>
            )}
            {showShareButton && (qrEnabled || smsEnabled) && !isMobile && (
              <ShareContainer
                popupTitle={popupTitle}
                qrEnabled={qrEnabled}
                qrTitle={qrTitle}
                smsEnabled={smsEnabled}
                smsTitle={smsTitle}
                smsText={smsText}
                tour={tour}
                target={target}
                actionPathFinished={actionPathFinished}
                ratingEnabled={ratingEnabled}
                startingPoint={startingPoint}
                rtl={rtl}
                country={country}
                policy={policy}
              />
            )}

            {!showShareButton && (qrEnabled || smsEnabled) && !DeviceManager.isMobile() && (
              <IconButton
                sx={{
                  display: 'flex',
                  width: '100%',
                  borderRadius: '8px',
                  border: '1px solid #D0D5DD',
                  color: '#2055FF',
                  gap: '6px',
                  background: '#ffff',
                  marginTop: '10px',
                  '&:hover': { background: '#ffff' },
                  direction: rtl ? 'rtl' : 'ltr',
                }}
                onClick={() => {
                  setShowShareButton(true);
                  setActionPathNavbarPlayButtonType('Play');
                  navigationManager.inAutoNavigate = false;
                }}
              >
                <PhoneIcon style={{ width: '20px', height: '20px' }} />
                <Typography
                  lineHeight="21px"
                  fontWeight="600"
                  sx={{ direction: rtl ? 'rtl' : 'ltr' }}
                >
                  {translation.navShareToPhone || 'Share to your phone'}
                </Typography>
              </IconButton>
            )}
          </Stack>
          {actionPathFinished && (
            <Stack
              direction="row"
              justifyContent="space-between"
              gap="6px"
              padding="14px"
              borderTop="1px solid var(--Gray-200, #EAECF0)"
              mt={{ xs: 'auto', sm: 'unset' }}
              sx={{ direction: rtl ? 'rtl' : 'ltr' }}
            >
              <Button
                style={{
                  padding: '0px 18px',
                  backgroundColor: '#fff',
                  color: '#344054',
                  border: '1px solid #D0D5DD',
                  height: '40px',
                }}
                onClick={handleBackToSearchBar}
              >
                <Typography sx={{ direction: rtl ? 'rtl' : 'ltr' }}>
                  {translation.navSearchAgain || 'Search Again'}
                </Typography>
              </Button>
              <Button style={{ padding: '0px 18px', height: '40px' }} onClick={handleReplay}>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '21px',
                    direction: rtl ? 'rtl' : 'ltr',
                  }}
                >
                  {translation.navReplay || 'Replay'}
                </Typography>
              </Button>
            </Stack>
          )}
          {detailedRouteEnabled && !actionPathFinished && !showShareButton && (
            <DetailedRoutes
              item={item || { tag: target }}
              setNavigationDestination={setNavigationDestination}
              isPreload={false}
              detailedRoutes={detailedRoutes}
              startingPoint={startingPoint}
              showDetailedRoutesOnly={showDetailedRoutesOnly}
              rtl={rtl}
              navigationType={navigationType}
              showCaseTemp={showCaseTemp}
            />
          )}
        </Wrapper>
      </Background>
    </>
  );
};
